import React, { Fragment, useState, useEffect, createContext, useContext, useRef } from 'react'
import { Dialog, Transition, Popover } from '@headlessui/react'
import {
    Bars3Icon,
    CalendarIcon,
    ChartPieIcon,
    DocumentDuplicateIcon,
    FolderIcon,
    HomeIcon,
    UsersIcon,
    XMarkIcon,
    ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { UserAuth } from '../context/AuthContext'
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom'
import '/Style.css'

import { motion, AnimatePresence } from 'framer-motion'
import {
    Tabs,
    TabsHeader,
    TabsBody,
    Tab,
    TabPanel,
} from "@material-tailwind/react"

import { db } from '../firebase'
import {
    query,
    collection,
    onSnapshot,
    updateDoc,
    orderBy,
    doc,
    where,
    addDoc,
    deleteDoc,
    serverTimestamp,
    getDoc
} from 'firebase/firestore'

import CompChat from './CompChat'
import Confetti from 'react-confetti'
import { CompHSChat } from './CompHSChat'

import Intercom from '@intercom/messenger-js-sdk';
import { logCustomEvent } from './UtilAnalytics'


// Create a context for navigation
const NavigationContext = createContext();

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// Move tabs definition outside the component to prevent recreation
const tabs = [
    {
        value: "path-type",
        label: "Home",
        desc: "Updates Content",
        link: "/app/home",
        icon: (
            <img src="/util/app/b3-admin-active-5.svg" className="w-5 h-5 " />
        )
    },
    {
        value: "path-Create",
        label: "Dive In & Create",
        desc: "Create Content",
        link: "/app/storyboard",
        icon: (
            <img src="/util/app/b3-admin-surf-2.svg" className="w-7 h-7 " />
        )
    },
    {
        value: "path-paths",
        label: "Paths",
        desc: "Paths Content",
        link: "/app/paths",
        icon: (
            <img src="/util/app/b3-admin-path-navigate.svg" className="w-5 h-5 " />
        )
    },
    {
        value: "path-Stories",
        label: "Theater",
        desc: "Theater Content",
        link: "/app/stories",
        icon: (
            <img src="/util/app/b3-admin-theater-3.svg" className="w-5 h-5 " />
        )
    },

    {
        value: "path-create",
        label: "Sharing",
        desc: "Sharing Content",
        link: "/app/share",
        icon: (
            <img src="/util/app/b3-admin-send2.svg" className="w-5 h-5 " alt="Slack" />
        )
    },
    {
        value: "path-storyboard",
        label: "Engagement",
        desc: "Analytics Content",
        link: "/app/insights",
        icon: (
            <img src="/util/app/b3-admin-analytics-11.svg" className="w-5 h-5" />
        )
    },
    {
        value: "path-connect",
        label: "Connect",
        desc: "Connect Content",
        link: "/app/storyboard",
        type: "connect",
        icon: (
            <img src="/util/app/Slack_icon_2019.svg" className="w-5 h-5" alt="Slack" />
        )
    },
    {
        value: "path-share",
        label: "Connect",
        desc: "Connect Content",
        link: "/app/share",
        type: "connect",
        icon: (
            <img src="/util/app/logo-box.svg" className="w-5 h-5" alt="Slack" />
        )
    },
];

// Memoize the entire header section
const Header = React.memo(function Header() {
    const { user, logOut } = UserAuth();
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    const handleSignOut = async () => {
        try {
            await logOut().then(() => navigate('/signin'));
        } catch (error) {
            console.log(error);
        }
    };

    const activeTab = React.useMemo(() => {
        const currentPath = location.pathname;
        let bestMatch = null;
        for (const tab of tabs) {
            if (currentPath.startsWith(tab.link)) {
                if (!bestMatch || tab.link.length > bestMatch.link.length) {
                    bestMatch = tab;
                }
            }
        }
        return bestMatch ? bestMatch.value : (tabs.length > 0 ? tabs[0].value : '');
    }, [location.pathname]);

    const handleTabClickMobile = (tab) => {
        logCustomEvent('nav_tab_click_mobile_' + tab.value, { tab: tab.value });
        document.title = tab.label;
        navigate(tab.link, { replace: true });
        setIsMobileNavOpen(false); // Close menu on navigation
    };

    return (
        <div className="absolute top-0 right-0 md:pl-10 md:pr-10 left-0 z-10 w-full bg-[#1C1C22] shadow-md">
            {/* Main Header Bar (Logo, User, Mobile Hamburger) */}
            <div className="relative z-50 flex items-center justify-between px-4 sm:px-6 h-16 md:py-12 bg-[#1C1C22]">
                {/* Left Side: Logo */}
                <div className="flex items-center">
                    <Link to="/app/home" className="flex-shrink-0 flex items-center">
                        <img src="/util/app/sp-test-logo-2.png" className="h-9 sm:h-9 md:h-11 mt-2  " alt="StoryPath" />
                    </Link>
                </div>

                {/* Middle Section: Desktop Tabs (Moved Here) */}
                <div className="hidden md:flex flex-grow items-center justify-center">
                    <AnimatedTabs activeTab={activeTab} />
                </div>

                {/* Right Side: User Info & Menu (Desktop) / Hamburger (Mobile) */}
                <div className="flex items-center">
                    {/* User Info & Menu (Desktop) */}
                    <div className="hidden md:flex items-center">
                        {/* User Name/Email (Desktop) */}
                        <div className="text-right mr-4 xl:block hidden">
                            <div className="text-base font-medium text-zinc-50">{user?.displayName}</div>
                            <div className="text-xs text-zinc-400">{user?.email || ''}</div>
                        </div>
                        {/* User Avatar */}
                        <img
                            className="h-14 w-14 rounded-lg border-2 border-zinc-800 bg-zinc-700"
                            src={user?.photoURL}
                            referrerPolicy="no-referrer"
                            alt=""
                        />
                        {/* User Popover Menu */}
                        <Popover className="relative ml-2 md:ml-3">
                            <Popover.Button className="p-1 rounded-full text-zinc-400 hover:text-zinc-100 focus:outline-none   ">
                               <EllipsisVerticalIcon className="w-5 h-5" aria-hidden="true" />
                            </Popover.Button>
                            {/* Popover Panel using Framer Motion as in original */}
                            <Popover.Panel className="absolute right-0 z-20 mt-2 w-48 origin-top-right">
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    transition={{ duration: 0.2 }}
                                    className="rounded-md bg-zinc-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-1"
                                >
                                    <Popover.Button as={Link} to="/app/account" className="block px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white w-full text-left rounded-md">
                                        Account
                                    </Popover.Button>
                                    <button
                                        onClick={handleSignOut}
                                        className="block w-full text-left px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-700 hover:text-white rounded-md"
                                    >
                                        Sign Out
                                    </button>
                                </motion.div>
                            </Popover.Panel>
                        </Popover>
                    </div>

                    {/* Hamburger for Mobile Nav */}
                    <div className="md:hidden ml-2"> {/* Adjusted margin */}
                        <button
                            onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                            aria-label="Open navigation"
                            className="text-zinc-300 hover:text-white focus:outline-none p-2 rounded-md" // Removed -ml-2
                        >
                            {isMobileNavOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Menu (Dropdown) using Framer Motion */}
            <AnimatePresence>
                {isMobileNavOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            key="mobile-nav-backdrop"
                            className="fixed inset-0 bg-black/50 z-30 md:hidden"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ type: "tween", ease: "easeInOut", duration: 0.2 }}
                            onClick={() => setIsMobileNavOpen(false)}
                        />
                        {/* Drawer Panel */}
                        <motion.div
                            key="mobile-nav-panel"
                            className="fixed top-0 right-0 h-full w-full sm:w-80 bg-[#1C1C22] z-40 shadow-xl md:hidden"
                            initial={{ x: "100%", opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            exit={{ x: "100%", opacity: 0 }}
                            transition={{ type: "tween", ease: "easeInOut", duration: 0.3 }}
                        >
                            <nav className="h-full overflow-y-auto pt-20 px-2 pb-4 space-y-1">
                                {tabs.filter(tab => tab.type !== "connect").map((tab) => (
                                    <Link
                                        key={tab.value}
                                        to={tab.link}
                                        onClick={() => handleTabClickMobile(tab)}
                                        className={classNames(
                                            'flex items-center px-3 py-3 rounded-md text-base font-medium transition-colors duration-150',
                                            activeTab === tab.value
                                                ? 'bg-sky-500 text-white'
                                                : 'text-zinc-300 hover:bg-zinc-700 hover:text-white'
                                        )}
                                        aria-current={activeTab === tab.value ? 'page' : undefined}
                                    >
                                        {tab.icon && React.cloneElement(tab.icon, { className: "w-5 h-5 mr-3 flex-shrink-0" })}
                                        {tab.label}
                                    </Link>
                                ))}
                                {/* Divider */}
                                <div className="pt-2 mt-2 border-t border-zinc-700/50"></div>
                                {/* Account Link */}
                                <Link
                                    to="/app/account"
                                    onClick={() => {
                                        logCustomEvent('nav_tab_click_mobile_account', { tab: 'account' });
                                        document.title = "Account";
                                        navigate("/app/account", { replace: true });
                                        setIsMobileNavOpen(false); // Close menu on navigation
                                    }}
                                    className={'flex items-center px-3 py-3 rounded-md text-base font-medium transition-colors duration-150 text-zinc-300 hover:bg-zinc-700 hover:text-white'}
                                >
                                    Account
                                </Link>
                                {/* Sign Out Button */}
                                <button
                                    onClick={() => {
                                        handleSignOut();
                                        setIsMobileNavOpen(false); // Close menu on action
                                    }}
                                    className={'flex items-center px-3 py-3 rounded-md text-base font-medium transition-colors duration-150 text-zinc-300 hover:bg-zinc-700 hover:text-white w-full text-left'}
                                >
                                    Sign Out
                                </button>
                            </nav>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* Desktop Tabs Bar - REMOVED as it's now part of the main header */}
            {/* <div className="hidden md:block ">
                <AnimatedTabs />
            </div> */}
        </div>
    );
});

// Update navAllStories to use a custom hook
export const useNavigation = () => {
    const handleTabClick = useContext(NavigationContext);

    const navAllStories = () => {
        if (handleTabClick) {
            handleTabClick(tabs[3]);
        } else {
            // Fallback to direct navigation if context is not available
            window.location.href = tabs[3].link;
            document.title = tabs[3].label;
        }
    };

    return { navAllStories };
};

// Memoize the AnimatedTabs
const AnimatedTabs = React.memo(function AnimatedTabs({ activeTab }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [showNotification, setShowNotification] = useState(false);
    const [indicatorStyle, setIndicatorStyle] = useState({ width: 0, left: 0 });
    const tabRefs = useRef({});

    //console.log("🔄 AnimatedTabs Re-render - activeTab prop:", activeTab);
    //console.log("🔄 Current pathname:", location.pathname);

    const handleTabClick = React.useCallback((tab) => {
        //  console.log("🖱️ Tab clicked:", tab.label, tab.value);
        logCustomEvent('nav_tab_click_' + tab.value, { tab: tab.value });
        if (tab.value === "path-type") {
            setShowNotification(false);
        }
        document.title = tab.label;
        navigate(tab.link, { replace: true });
    }, [navigate]);

    // Update indicator position and size based on active tab
    useEffect(() => {
        const activeTabRef = tabRefs.current[activeTab];
        if (activeTabRef) {
            const { offsetWidth, offsetLeft } = activeTabRef;
            setIndicatorStyle({
                width: offsetWidth,
                left: offsetLeft
            });
        }
    }, [activeTab]);

    const showConnect = location.pathname.includes('/app/storyboard') ||
        location.pathname.includes('/app/share');

    useEffect(() => {
        // Listen for the AI message event
        const timer = setTimeout(() => {
            setShowNotification(true);
        }, 4000); // Same timing as the AI message

        return () => clearTimeout(timer);
    }, []);

    return (
        <NavigationContext.Provider value={handleTabClick}>
            {/* Custom Tabs Implementation */}
            <div className="flex justify-center">
                <div className="relative flex bg-transparent border-b-0">
                    {/* Active Tab Indicator */}
                    <div 
                        className="absolute bottom-0 h-full bg-black/20 rounded-lg transition-all duration-300 ease-in-out"
                        style={{
                            width: `${indicatorStyle.width}px`,
                            left: `${indicatorStyle.left}px`,
                        }}
                    />
                    
                    {/* Tab Buttons */}
                    <div className="flex relative z-10">
                        {tabs.map((tab) => (
                            // Only render non-connect tabs here
                            tab.type !== "connect" && (
                                <button
                                    key={tab.value}
                                    ref={(el) => tabRefs.current[tab.value] = el}
                                    onClick={() => handleTabClick(tab)}
                                    className={`px-3 py-3 text-base md:text-lg w-fit mr-1 transition-colors duration-200 relative
                                        ${activeTab === tab.value
                                            ? "text-zinc-50"
                                            : "text-zinc-400 hover:text-zinc-200"}`}
                                >
                                    <div className="flex items-center gap-2 md:gap-4 pr-1.5">
                                        <div className={`transition-opacity duration-200 ${activeTab === tab.value ? "opacity-100" : "opacity-50"
                                            }`}>
                                            {React.cloneElement(tab.icon, { className: "h-5 w-5" })}
                                        </div>
                                        {tab.label}
                                    </div>
                                </button>
                            )
                        ))}
                    </div>
                </div>
            </div>
        </NavigationContext.Provider>
    );
});

// Memoize the content section to prevent re-renders
// const Content = React.memo(function Content() {
//     return (
//         <div className="pt-[200px] px-16 overflow-y-auto">
//             <Outlet />
//         </div>
//     );
// });

function Content() {
    return (
        <div className="mt-12 md:pt-20 md:px-16 px-4 sm:px-6 pb-20 overflow-y-auto">
            <Outlet />
        </div>
    );
}

// Add this new component before AdminNavV2
// const ChatBubble = React.memo(function ChatBubble() {
//     const [isOpen, setIsOpen] = useState(false)

//     return (
//         <>
//             {/* Chat Bubble Button */}
//             <motion.div
//                 className="fixed bottom-8 right-8 z-50"
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//             >
//                 <button
//                     onClick={() => setIsOpen(true)}
//                     className="flex items-center gap-2 bg-zinc-900 text-white px-4 py-4 rounded-full shadow-lg hover:bg-zinc-600 cursor-pointer"
//                 >
//                     <ChatBubbleLeftRightIcon className="h-8 w-8" />
//                     {/* <span>Help or Question</span> */}
//                 </button>
//             </motion.div>

//             {/* Chat Modal */}
//             <Transition show={isOpen} as={Fragment}>
//                 <Dialog
//                     as="div"
//                     className="relative z-50"
//                     onClose={() => setIsOpen(false)}
//                 >
//                     <Transition.Child
//                         as={Fragment}
//                         enter="ease-out duration-300"
//                         enterFrom="opacity-0"
//                         enterTo="opacity-100"
//                         leave="ease-in duration-200"
//                         leaveFrom="opacity-100"
//                         leaveTo="opacity-0"
//                     >
//                         <div className="fixed inset-0 bg-black/25" />
//                     </Transition.Child>

//                     <div className="fixed inset-0">
//                         <div className="absolute bottom-0 right-0 p-8">
//                             <Transition.Child
//                                 as={Fragment}
//                                 enter="ease-out duration-300"
//                                 enterFrom="translate-y-full opacity-0"
//                                 enterTo="translate-y-0 opacity-100"
//                                 leave="ease-in duration-200"
//                                 leaveFrom="translate-y-0 opacity-100"
//                                 leaveTo="translate-y-full opacity-0"
//                             >
//                                 <Dialog.Panel className="w-[400px] transform overflow-hidden rounded-xl bg-white shadow-xl transition-all">
//                                     <div className="relative">
//                                         <button
//                                             onClick={() => setIsOpen(false)}
//                                             className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
//                                         >
//                                             <XMarkIcon className="h-5 w-5" />
//                                         </button>
//                                         <div className="h-[600px]">
//                                             Help chat to Slack here!
//                                         </div>
//                                     </div>
//                                 </Dialog.Panel>
//                             </Transition.Child>
//                         </div>
//                     </div>
//                 </Dialog>
//             </Transition>
//         </>
//     )
// })

const ChatBubble = React.memo(function ChatBubble() {
    const { user } = UserAuth()
    const location = useLocation(); // This will update whenever the URL changes

    // Define paths where the chat bubble should not appear
    const excludedPaths = [
        /\/app\/stories\/[^/]+\/edit\//,  // Your existing pattern
        // Add more patterns as needed
        ///\/app\/some-other-path\//,
        ///\/app\/another-path\//
    ];

    // Check if current path matches any excluded pattern
    const shouldHide = excludedPaths.some(pattern => pattern.test(location.pathname));

    useEffect(() => {
        //console.log('shouldHide', shouldHide)
        // Only initialize Intercom if we're showing the chat bubble
        if (!shouldHide) {
            Intercom({
                app_id: 'g2h9i6in',
                user_id: user?.uid,
                name: user?.displayName,
                email: user?.email,
                created_at: user?.metadata?.createdAt,
            });
        }
    }, [user, shouldHide, location.pathname]);

    // Don't render anything if we're on an excluded path
    if (shouldHide) {
        return null;
    }

    return (
        <>
            {/* Your chat bubble content here */}
        </>
    );
});

// Add this new component for the welcome modal
const WelcomeModal = React.memo(function WelcomeModal({ isOpen, setIsOpen }) {
    const { user } = UserAuth();
    const [dontShowAgain, setDontShowAgain] = useState(true);

    const handleClose = async () => {
        if (dontShowAgain && user?.uid) {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                await updateDoc(userDocRef, {
                    "onboarding.brevit3-upgrade": true
                });
                //console.log("User preference saved");
            } catch (error) {
                console.error("Error updating user preferences:", error);
            }
        }
        setIsOpen(false);
    };

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-2/3 transform overflow-hidden rounded-2xl  bg-zinc-900  p-12 text-left align-middle  transition-all">
                                {/* <Dialog.Title
                                    as="h3"
                                    className="text-2xl font-bold leading-6 text-white mb-4 flex items-center"
                                >
                                    

                                </Dialog.Title> */}

                                <div className="mt-4 text-zinc-300 text-lg col-span-2">
                                    <div className="px-8">
                                        <img src="/util/app/sp-test-logo-2.png" className="h-16 mr-3" alt="StoryPath Logo" />
                                        <div className='text-3xl font-semibold leading-6 text-orange-300 mb-4 mt-12  items-center'>
                                        McKinsey + Pixar for Enterprise Revenue Teams
                                        <p className="mb-12 pt-4 text-zinc-400 text-lg font-normal"> Welcome to StoryPath. Over the last 18 months, we've been quietly working on something new. Think *McKinsey insight meets Pixar storytelling*, purpose-built **by GTM, for GTM**. StoryPath is an AI-native platform designed to solve for the messy middle, those high-stakes moments in a deal when clarity, confidence, and internal alignment are crucial.</p>
                                    </div>
                                    </div>



                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                                        <div className="bg-zinc-800 p-8 rounded-lg">
                                            <h4 className="text-zinc-50   mb-2 flex items-center text-xl">
                                                <img src="/util/app/b3-admin-story-5.svg" className="h-10 mr-2 -ml-1" />
                                                Storytelling Deep Research
                                            </h4>
                                            <p className="text-base text-zinc-400  mt-4">Let's us help with the heavy lifting and generate research, insights, strategies and stories based on your needs.</p>
                                        </div>

                                        <div className="bg-zinc-800 p-8 rounded-lg">
                                            <h4 className="text-zinc-50  mb-2 flex items-center text-xl">
                                                <img src="/util/app/b3-admin-context-analysis.svg" className="h-10 mr-2 -ml-1" />
                                                Contextual Analysis
                                            </h4>
                                            <p className="text-base text-zinc-400  mt-4">
                                                Our intelligence is focused on providing relevant insights and suggestions for your storytelling based on your direction and input.
                                            </p>
                                        </div>

                                        <div className="bg-zinc-800 p-8 rounded-lg">
                                            <h4 className="text-zinc-50   mb-2 flex items-center text-xl">
                                                <img src="/util/app/b3-admin-story-settings.svg" className="h-8 mr-3 " />
                                                Customizable Tone &amp; Style
                                            </h4>
                                            <p className="text-base text-zinc-400 mt-4">Your stories will be created based on tone and style parameters you set, making them easy to create and understand.</p>
                                        </div>

                                        <div className="bg-zinc-800 p-8 rounded-lg">
                                            <h4 className="text-zinc-50  mb-2 flex items-center text-xl">
                                                <img src="/util/app/b3-story-bite.svg" className="h-10 mr-2 -ml-1" />
                                                Story Bites Generation
                                            </h4>
                                            <p className="text-base text-zinc-400 mt-4">One click generation of bite-sized content to highlight the value of the business transform your solutions provides to customers.</p>
                                        </div>

                                        <div className="  p-8 rounded-lg col-span-2">
                                            <h4 className="text-zinc-50  mb-2 flex items-center text-xl">
                                                <img src="/util/app/b3-admin-support-chat.png" className="h-8 mr-3" />
                                                Questions, Feedback and Support
                                            </h4>
                                            <p className="text-base text-zinc-400 mt-4">We're here to help.  Send us your questions, feedback and support requests by clicking the chat icon in the bottom right corner.</p>
                                        </div>
                                    </div>

                                    {/* <p className="text-base text-zinc-400 italic">All your existing content and settings have been preserved in this transition.</p> */}
                                </div>

                                <div className="mt-12 flex flex-col sm:flex-row items-center justify-between">
                                    <label className="flex items-center text-zinc-300 mb-4 sm:mb-0 ml-8">
                                        <input
                                            type="checkbox"
                                            checked={dontShowAgain}
                                            onChange={() => setDontShowAgain(!dontShowAgain)}
                                            className="mr-2 h-4 w-4 rounded border-gray-300 text-sky-500 focus:ring-sky-500"
                                        />
                                        Don't show again
                                    </label>

                                    <button
                                        type="button"
                                        className="inline-flex justify-center rounded-md border border-transparent bg-sky-500 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-sky-400 "
                                        onClick={handleClose}
                                    >
                                        Get Started with StoryPath
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
});

// Main component
export default function AdminNavV2() {
    const location = useLocation();
    const { user } = UserAuth();
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    // Check if the current path matches the pattern we want to exclude
    const isEditPage = /\/app\/stories\/[^/]+\/edit\//.test(location.pathname);

    useEffect(() => {
        const checkUserCreationDate = async () => {
            if (user?.uid) {
                try {
                    // Check if user was created before March 1st, 2025
                    const userCreationTime = new Date(parseInt(user.metadata.createdAt));
                    const cutoffDate = new Date('2025-03-01');

                    if (userCreationTime < cutoffDate) {
                        // Check if user has already seen the modal
                        const userDocRef = doc(db, 'users', user.uid);
                        const userDoc = await getDoc(userDocRef);

                        if (!userDoc.exists() ||
                            !userDoc.data().onboarding ||
                            !userDoc.data().onboarding['brevit3-upgrade']) {
                            setShowWelcomeModal(true);
                        }
                    }
                } catch (error) {
                    console.error("Error checking user creation date:", error);
                }
            }
        };

        checkUserCreationDate();
    }, [user]);

    return (
        <div className="">
            <Header />





            {/* <div className="pt-4   mt-12  ">
                <AnimatedTabs />
            </div> */}
            <div className="">
            <Content />
            {/* {!isEditPage && <ChatBubble />} */}
            <WelcomeModal isOpen={showWelcomeModal} setIsOpen={setShowWelcomeModal} />
            </div>
        </div>
    );
}